import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Arrow from '../../../assets/svg/Arrow';
import Node from './Node';

import colors from '../../../styling/colors';

const LARGE_SECTION_HEIGHT = 3.5;
const SMALL_SECTION_HEIGHT = 2;

const ChildNodesContainer = styled.div`
  max-height: ${({ maxHeight }) => `${maxHeight}rem`};

  /** styles for open/close **/
  transition: max-height 0.3s ease-in-out;
  overflow: hidden;
`;

const Container = styled.div``;

const SubTitle = styled.span`
  color: ${colors('gatsby')};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.125rem;
  margin-left: 0.125rem;
  text-transform: uppercase;
`;

const Title = styled.span`
  display: inline-block;
`;

const StyledNode = styled(Node)`
  ${({ isActive, level }) => {
    switch (level) {
      case 2:
        return `
          height: ${SMALL_SECTION_HEIGHT}rem;
          line-height: normal;
          margin-left: 2rem;
          padding: 0.5rem 1.5rem;
        `;
      case 1:
        return `
          ${!isActive ? `border-bottom: 1px solid ${colors('alto')};` : ``};
          height: ${LARGE_SECTION_HEIGHT}em;
          margin-left: 2rem;
          padding: 1rem 1.5rem 1rem 0;
        `;
      default:
        return `
          ${!isActive ? `border-bottom: 1px solid ${colors('alto')};` : ``};
          height: ${LARGE_SECTION_HEIGHT}rem;
          margin-left: 0;
          padding: 1rem 1.5rem;
        `;
    }
  }}
`;

const getMaxHeight = nodes => {
  let height = 0;

  if (nodes && nodes.length) {
    height += nodes.length * LARGE_SECTION_HEIGHT;

    let childHeight = 0;
    nodes.map(node => {
      if (node.childNodes.length > childHeight) {
        childHeight = node.childNodes.length;
      }
    });
    height += childHeight * SMALL_SECTION_HEIGHT;
  }
  return height;
};

const AccordionSection = ({
  className,
  isActive,
  level = 0,
  node,
  onClick,
}) => {
  const [activeKey, setActiveKey] = useState(null);

  const { title } = node;
  let { childNodes } = node;

  // The `childNode` of flyers are `Browse by category` which then has the categories we want
  if (title === 'Flyer') {
    childNodes = childNodes[0].childNodes;
  }
  const arrowDirection = isActive ? 'up' : 'down';
  const isLeaf = childNodes.length === 0;

  const maxHeight = isActive ? getMaxHeight(childNodes) : 0;

  const handleNodeClick = () => {
    onClick(title);
    setActiveKey(null);
  };

  return (
    <Container className={className}>
      <StyledNode
        isActive={isActive}
        isLeaf={isLeaf}
        level={level}
        node={node}
        onClick={() => handleNodeClick(node)}
      >
        <Title>
          {title}
          {title === 'Flyer' && <SubTitle> New!</SubTitle>}
        </Title>
        {!isLeaf && (
          <Arrow
            className="stack-forward-button"
            direction={arrowDirection}
            strokeColor={colors('mineshaft')}
          />
        )}
      </StyledNode>
      {!isLeaf && (
        <ChildNodesContainer maxHeight={maxHeight}>
          {childNodes.map(node => {
            const isKeyActive = activeKey === node.title;
            return (
              <AccordionSection
                isActive={isKeyActive}
                level={level + 1}
                node={node}
                onClick={() => setActiveKey(isKeyActive ? null : node.title)}
              />
            );
          })}
        </ChildNodesContainer>
      )}
    </Container>
  );
};

AccordionSection.propTypes = {
  className: PropTypes.string,
  node: PropTypes.shape({
    href: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

AccordionSection.defaultProps = {
  childNodes: [],
};

export default React.memo(AccordionSection);
